import _ from 'lodash'
import { APIServer } from '@/app/data/api'
import { useAuthStore } from '@/app/data/auth'
import { CloudFile } from '@/lib/CloudFile'
import { base64ToFile } from '@/lib/Functions/files'
import { getLocalDatabaseRandomKey } from '@/app/function'
import { GCS_ROOT_PATH, isApp } from '@/index'

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { convertFileToWebp } from '@/app/camera'
import { SQL } from '@/lib/Sqlite'
import { prepareSQLiteValue } from '@/lib/Sqlite/schema'
import {
  parseRow,
  uploadPathGenerator,
  handleCapturedPhotoClone,
  syncCacheWithServerUrl,
  sortByKeys,
  getLastSortKey,
} from '@/app/util'
import { getUnixtime } from '@/lib/Functions'
import { Network } from '@capacitor/network'
import { syncQueue, JobPriority } from '@/app/sync'
import { filterStaleMergedItems } from '@/app/sync/mergeWriteGuard'
import { hasInFlightJob, isRowDeletedLocally } from '@/app/sync/jobGuards'
import { SyncManager } from '@/app/sync/syncManager'
import { AppFileManager } from '../../file'
import { LocalFileResourcePath } from '../../camera/type'
import {
  FileMissingError,
  registerFileMissing,
  clearFileMissingCount,
  isFileNotFoundError,
  isFileMissingConfirmed,
} from './photoFileGuard'
import { FacilityPhoto } from '../types'
import type { PhotoDrawing } from '../../photoDrawing/types'
import { isSamePhotoDrawing } from '../../photoDrawing/validate'
import Merge from '../../../lib/Task/merge'
import SQLite from '../../../lib/Task/sqlite'
import { SQLiteEventBus, SQLiteUpdateEvent } from '@/lib/sqliteEventBus'
import { useAllTaskStore } from '../../store/task'

const storeKey = 'facility_photos'

//TODO 0917 android-file, android-local-file 관련 수정해야함 local-file이 맞음

export const useFacilityPhotoStore = defineStore('facility_photos', () => {
  const facilityPhotos = ref<FacilityPhoto[]>([])
  const currentTaskDatabaseId = ref<string>('')

  // SQLite 이벤트 리스너 참조 (중복 등록 방지 및 cleanup용)
  let currentListener: ((event: SQLiteUpdateEvent) => void) | null = null

  const notUploadedFacilityPhotos = computed(() => {
    return facilityPhotos.value.filter(item => item.modifiedFromLocal)
  })
  async function uploadNotUploaded(): Promise<void> {
    if (notUploadedFacilityPhotos.value.length > 0) {
      await requestUpdate(notUploadedFacilityPhotos.value)
    }
  }
  const getFacilityPhotoListOnTask = computed(() => {
    return (branchId: string): FacilityPhoto[] => {
      return facilityPhotos.value.filter(
        item => item.facility_id.toString() == branchId && !item.deleted_at,
      )
    }
  })

  const getAllFacilityBranchImages = computed(() => {
    return facilityPhotos.value.filter(item => !item.deleted_at)
  })
  async function individualSetLocalModified(
    localStringId: string, // 시그니처 변경
    keyValues: { key: string; value: unknown }[] = [],
  ): Promise<void> {
    const db = await SQL.getDatabase()

    // id 교체 시 동일 localStringId의 유령 row가 남아 있으면 먼저 정리
    // (두 row가 같은 id로 동시 갱신되며 PRIMARY KEY 충돌로 UPDATE 전체가 실패하는 것을 방지)
    if (keyValues.some(keyValue => keyValue.key === 'id')) {
      const duplicated = await db.query(`SELECT * FROM ${storeKey} WHERE localStringId = ?`, [
        localStringId,
      ])
      if (duplicated.values && duplicated.values.length > 1) {
        await removeGhostRows(
          duplicated.values.map((row: FacilityPhoto) => parseRow(row)) as FacilityPhoto[],
        )
      }
    }

    let sql = `UPDATE ${storeKey} SET `
    for (let i = 0; i < keyValues.length; i++) {
      const keyValue = keyValues[i]
      sql += `${keyValue.key} = ?`
      if (i < keyValues.length - 1) {
        sql += ', '
      }
    }
    sql += ' WHERE localStringId = ?' // WHERE 절 변경
    // 값 정규화: iOS native bridge가 number를 TEXT 컬럼에 "557.0"으로 저장하는 것을 차단
    await db.run(sql, [...keyValues.map(item => prepareSQLiteValue(item.value)), localStringId])

    // 이벤트 발행 (리스너 완료 대기)
    await SQLiteEventBus.getInstance().emitUpdate('facility_photos', 'update', [localStringId])
  }

  /**
   * SQLite 이벤트 리스너 설정
   * @param taskId - 현재 task ID
   */
  function setupSQLiteListener(taskId: string): void {
    // 기존 리스너 해제 (중복 등록 방지)
    if (currentListener) {
      SQLiteEventBus.getInstance().offTableUpdated('facility_photos', currentListener)
    }

    currentListener = async (event: SQLiteUpdateEvent): Promise<void> => {
      try {
        const db = await SQL.getDatabase()

        if (event.type === 'bulk' || event.ids.length === 0) {
          // 대량 업데이트: 전체 로드
          const localData = await db.query(`SELECT * FROM ${storeKey} WHERE task_id = ?`, [taskId])
          facilityPhotos.value = localData.values?.map((row: unknown) =>
            parseRow(row),
          ) as FacilityPhoto[]
        } else {
          // 개별 업데이트: 해당 항목만 갱신
          for (const id of event.ids) {
            const result = await db.query(
              `SELECT * FROM ${storeKey} WHERE localStringId = ? AND task_id = ?`,
              [id, taskId],
            )

            if (result.values && result.values.length > 0) {
              const updatedItem = parseRow(result.values[0]) as FacilityPhoto
              const index = facilityPhotos.value.findIndex(p => p.localStringId === id)

              if (updatedItem.deleted_at) {
                // 삭제된 항목은 배열에서 제거
                if (index !== -1) facilityPhotos.value.splice(index, 1)
              } else if (index !== -1) {
                // 기존 항목 업데이트
                facilityPhotos.value[index] = updatedItem
              } else {
                // 새 항목 추가
                facilityPhotos.value.push(updatedItem)
              }
            } else {
              // 쿼리 결과 없음 = 삭제됨
              const index = facilityPhotos.value.findIndex(p => p.localStringId === id)
              if (index !== -1) facilityPhotos.value.splice(index, 1)
            }
          }
        }
      } catch (error) {
        console.error('[FacilityPhotoStore] Failed to process SQLite event:', error)
      }
    }

    SQLiteEventBus.getInstance().onTableUpdated('facility_photos', currentListener)
  }

  async function getServerData(
    taskId: string,
    facilityPhotos?: FacilityPhoto[],
  ): Promise<FacilityPhoto[]> {
    if (!taskId) return []
    if (facilityPhotos) {
      return facilityPhotos.map(item => {
        return {
          ...item,
          localStringId: item.id.toString(),
          modifiedFromLocal: false,
        } as FacilityPhoto
      })
    }
    try {
      const data: FacilityPhoto[] = await APIServer.getFacilityBranchPhotoListByTaskId(taskId)
      return data
        .filter(item => !item.deleted_at)
        .map(item => {
          return {
            ...item,
            localStringId: item.id.toString(),
            modifiedFromLocal: false,
          } as FacilityPhoto
        })
    } catch (error) {
      console.error('Error getting server:', error)
      throw error
    }
  }

  const addApiQueue = async (
    itemsToProcess: { item: FacilityPhoto; itemId: string }[],
  ): Promise<void> => {
    if (itemsToProcess.length === 0) return

    for (const { item } of itemsToProcess) {
      if (!item.modifiedFromLocal) continue
      addFacilityImageJobToQueue(item)
    }
  }

  const requestUpdate = async (datas: Partial<FacilityPhoto>[]): Promise<void> => {
    const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(
      datas as unknown as FacilityPhoto[],
      storeKey,
    )
    await SQLite.updateSqliteOnly(itemsToProcess, columns, currentTaskDatabaseId.value, storeKey)
    await addApiQueue(itemsToProcess as { item: FacilityPhoto; itemId: string }[])
  }

  /**
   * FacilityPhoto 동기화 작업을 SyncQueue에 추가하는 헬퍼 함수
   * @param item 동기화할 FacilityPhoto 데이터
   */
  /**
   * 파일 부재 사진 처리: 유예 중에는 FileMissingError로 재시도를 유도하고,
   * 부재 확정 시 동기화 대상에서 제외한다 (행과 '기기에서 삭제됨' 배지는 유지)
   * @param data 대상 사진
   * @param localFilePath 누락된 로컬 파일 경로
   * @returns 확정 시 동기화 종료 처리된 사진 (job 성공으로 처리)
   */
  async function handleMissingPhotoFile(
    data: FacilityPhoto,
    localFilePath: string,
  ): Promise<FacilityPhoto> {
    if (registerFileMissing(data.localStringId, '시설') === 'retry') {
      throw new FileMissingError(`File not found: ${localFilePath}`)
    }
    await individualSetLocalModified(data.localStringId, [
      { key: 'modifiedFromLocal', value: false },
    ])
    return { ...data, modifiedFromLocal: false }
  }

  async function addFacilityImageJobToQueue(item: FacilityPhoto): Promise<void> {
    const isNew = String(item.id).includes('_need_to_upload')
    const isDeleted = item.deleted_at
    const needsFileUpload =
      item.url.startsWith('local-file://') ||
      item.url.startsWith('data:image') ||
      item.url.startsWith('android-local-file:/') ||
      item.url.startsWith('android-file:/')

    let executeCallback: (data: FacilityPhoto) => Promise<FacilityPhoto | void>
    let jobPriority = JobPriority.NORMAL

    if (isDeleted) {
      // --- 삭제 작업 ---
      // 사진이 삭제되면 파일 부재 이력도 함께 정리
      clearFileMissingCount(item.localStringId)
      // 서버에 생성된 적 없고 진행 중인 업로드 job도 없는 사진은 서버에 알릴 필요가 없음
      if (isNew && !hasInFlightJob(item.localStringId)) {
        await individualSetLocalModified(item.localStringId, [
          { key: 'modifiedFromLocal', value: false },
        ])
        return
      }
      executeCallback = async (data: FacilityPhoto): Promise<void> => {
        // 실행 시점 기준 서버 미생성 사진(업로드가 삭제로 무효화된 경우): 서버 호출 없이 종료
        if (String(data.id).includes('_need_to_upload')) {
          await individualSetLocalModified(data.localStringId, [
            { key: 'modifiedFromLocal', value: false },
          ])
          return
        }
        await APIServer.deleteFacilityBranchPhoto([{ id: parseInt(data.id.toString()) }])
        // onSuccess의 mfl 정리는 enqueue 시점 isNew에 의존하므로 여기서 직접 완료 처리
        await individualSetLocalModified(data.localStringId, [
          { key: 'modifiedFromLocal', value: false },
        ])
      }
    } else if (isNew) {
      // --- 생성 작업: 파일 먼저 업로드 → 레코드 생성 ---
      jobPriority = JobPriority.LOW // 파일 업로드는 우선순위 낮춤
      executeCallback = async (data: FacilityPhoto): Promise<FacilityPhoto> => {
        // 업로드 전에 로컬에서 삭제된 사진: 서버에 생성하지 않고 동기화 종료
        if (data.deleted_at) {
          await individualSetLocalModified(data.localStringId, [
            { key: 'modifiedFromLocal', value: false },
          ])
          return data
        }
        const facilityBranchId = data.facility_id
        // 신규 로직: 파일 업로드를 먼저 수행
        if (needsFileUpload) {
          // storage://로 시작하면 이미 GCS 업로드 완료 (SQLite만 업데이트된 상태)
          // 이 경우 MySQL 레코드만 생성하면 됨
          if (data.url.startsWith('storage://')) {
            const form = {
              facility_id: facilityBranchId,
              memo: data.memo,
              sort_key: data.sort_key,
              url: data.url, // 이미 업로드된 GCS URL 사용
              // 첫 업로드 전에 그린 그림은 생성 페이로드로만 올라간다
              drawing: data.drawing ?? null,
              // 오프라인 촬영 후 동기화 시 서버 도착 시각이 아닌 실제 촬영 시각 보존
              created_at: data.created_at,
              updated_at: data.updated_at,
              fields_updated_at: data.fields_updated_at,
            }

            const [photoResult] = await APIServer.addFacilityBranchPhoto([form])
            if (!photoResult?.id) {
              throw new Error('Failed to create photo record on server.')
            }

            // onIdMap 실패 시에도 modifiedFromLocal=false가 보장됨
            // (단, 업로드 도중 로컬 삭제가 끼어든 경우 mfl을 유지해 삭제 job이 이어서 전파)
            const updateKeys: { key: string; value: unknown }[] = [
              { key: 'id', value: photoResult.id },
            ]
            if (!(await isRowDeletedLocally(storeKey, data.localStringId))) {
              updateKeys.push({ key: 'modifiedFromLocal', value: false })
            }
            await individualSetLocalModified(data.localStringId, updateKeys)

            return { ...data, id: photoResult.id, url: data.url }
          }

          // 파일명 생성: {facilityBranchId}_{localStringId}_{timestamp}.webp
          const tempFileName = `${facilityBranchId}_${data.localStringId}_${Date.now()}.webp`
          let file: File | undefined
          let webpBase64: string | undefined
          let localFilePath: LocalFileResourcePath | undefined

          if (
            data.url.startsWith('local-file://') ||
            data.url.startsWith('android-local-file:/') ||
            data.url.startsWith('android-file:/')
          ) {
            localFilePath = data.url as LocalFileResourcePath

            // 파일 존재 확인 (확인된 404만 missing 판정)
            if (!(await AppFileManager.checkFileExists(localFilePath))) {
              return handleMissingPhotoFile(data, localFilePath)
            }

            try {
              const converted = await convertFileToWebp({ resourcePath: localFilePath })
              if (!converted.value) {
                throw new Error('File conversion to WebP failed.')
              }
              webpBase64 = converted.value
            } catch (error) {
              // 사전 확인이 통과해도 네이티브 변환의 파일 없음(ENOENT)은 부재 처리로 위임
              if (!isFileNotFoundError(error)) throw error
              return handleMissingPhotoFile(data, localFilePath)
            }
            clearFileMissingCount(data.localStringId)
            file = await base64ToFile(webpBase64, tempFileName)
          } else if (data.url.startsWith('data:image')) {
            file = await base64ToFile(data.url, tempFileName)
          }

          if (!file) throw new Error('File conversion failed for new facility image.')

          const splitPath = localFilePath ? localFilePath.split('_') : []
          const taskDatabaseId = splitPath[1] ?? currentTaskDatabaseId.value ?? 'local'
          const facilityBranchDatabaseId = splitPath[2] ?? 'local'

          const uploadPath = uploadPathGenerator(taskDatabaseId, facilityBranchDatabaseId)
          // 1단계: 파일을 버킷에 먼저 업로드
          const resourceUrl = await CloudFile.uploadFile(
            'homecheck-service',
            `${GCS_ROOT_PATH}/${uploadPath}/${file.name}`,
            file,
          )
          if (!resourceUrl) throw new Error('Cloud upload failed for new facility image.')

          // storage://로 시작하는지 확인 (전제조건 1)
          if (!resourceUrl.startsWith('storage://')) {
            throw new Error(`Upload failed: invalid URL format (${resourceUrl})`)
          }

          // 2단계: GCS 업로드 성공 시 SQLite와 job 데이터에 즉시 url 반영
          // 이후 재시도가 storage:// 분기로 진입해 파일 재업로드·서버 레코드 중복 생성을 막는다
          await individualSetLocalModified(data.localStringId, [{ key: 'url', value: resourceUrl }])
          data.url = resourceUrl

          // 촬영 사진을 앱 디렉토리로 복사
          await handleCapturedPhotoClone({ ...data, resourcePath: localFilePath })

          // 서버 파일명으로 캐시에 복사 (오프라인)
          await syncCacheWithServerUrl(localFilePath || '', resourceUrl)

          // 3단계: 파일 업로드 성공 후, 버킷 경로로 레코드 생성
          const form = {
            facility_id: facilityBranchId,
            memo: data.memo,
            url: resourceUrl, // 버킷 경로로 생성
            sort_key: data.sort_key,
            // 첫 업로드 전에 그린 그림은 생성 페이로드로만 올라간다
            drawing: data.drawing ?? null,
            // 오프라인 촬영 후 동기화 시 서버 도착 시각이 아닌 실제 촬영 시각 보존
            created_at: data.created_at,
            updated_at: data.updated_at,
            fields_updated_at: data.fields_updated_at,
          }

          const [photoResult] = await APIServer.addFacilityBranchPhoto([form])
          if (!photoResult?.id) {
            throw new Error('Failed to create photo record on server.')
          }

          // 최종 성공 결과를 반환
          return { ...data, id: photoResult.id, url: resourceUrl }
        }

        // 파일 업로드가 필요 없는 경우 (드문 케이스)
        const form = {
          facility_id: facilityBranchId,
          memo: data.memo,
          url: data.url,
          sort_key: data.sort_key,
          // 첫 업로드 전에 그린 그림은 생성 페이로드로만 올라간다
          drawing: data.drawing ?? null,
          // 오프라인 촬영 후 동기화 시 서버 도착 시각이 아닌 실제 촬영 시각 보존
          created_at: data.created_at,
          updated_at: data.updated_at,
          fields_updated_at: data.fields_updated_at,
        }
        const [photoResult] = await APIServer.addFacilityBranchPhoto([form])
        return { ...data, id: photoResult.id }
      }
    } else {
      // --- 업데이트 작업 ---
      if (needsFileUpload) {
        jobPriority = JobPriority.LOW // 파일 업로드는 우선순위 낮춤
        executeCallback = async (data: FacilityPhoto): Promise<FacilityPhoto> => {
          // storage://로 시작하면 이미 업로드 완료된 것으로 간주 (전제조건 1)
          if (data.url.startsWith('storage://')) {
            return data
          }

          const fileName = `${data.id}_${Date.now()}.webp`
          let file: File | undefined
          let webpBase64: string | undefined
          let localFilePath: LocalFileResourcePath | undefined

          if (
            data.url.startsWith('local-file://') ||
            data.url.startsWith('android-local-file:/') ||
            data.url.startsWith('android-file:/')
          ) {
            localFilePath = data.url as LocalFileResourcePath

            // 파일 존재 확인 (확인된 404만 missing 판정)
            if (!(await AppFileManager.checkFileExists(localFilePath))) {
              return handleMissingPhotoFile(data, localFilePath)
            }

            try {
              const converted = await convertFileToWebp({ resourcePath: localFilePath })
              if (!converted.value) {
                throw new Error('File conversion to WebP failed.')
              }
              webpBase64 = converted.value
            } catch (error) {
              // 사전 확인이 통과해도 네이티브 변환의 파일 없음(ENOENT)은 부재 처리로 위임
              if (!isFileNotFoundError(error)) throw error
              return handleMissingPhotoFile(data, localFilePath)
            }
            clearFileMissingCount(data.localStringId)
            file = await base64ToFile(webpBase64, fileName)
          } else if (data.url.startsWith('data:image')) {
            file = await base64ToFile(data.url, fileName)
          }

          if (!file) throw new Error('File conversion failed for update facility image.')
          const splitPath = localFilePath ? localFilePath.split('_') : []
          const taskDatabaseId = splitPath[1] ?? currentTaskDatabaseId.value ?? 'local'
          const facilityBranchDatabaseId = splitPath[2] ?? 'local'

          const uploadPath = uploadPathGenerator(taskDatabaseId, facilityBranchDatabaseId)
          // 파일을 먼저 업로드
          const resourceUrl = await CloudFile.uploadFile(
            'homecheck-service',
            `${GCS_ROOT_PATH}/${uploadPath}/${file.name}`,
            file,
          )
          if (!resourceUrl) throw new Error('Cloud upload failed for update facility image.')

          // storage://로 시작하는지 확인 (전제조건 1)
          if (!resourceUrl.startsWith('storage://')) {
            throw new Error(`Upload failed: invalid URL format (${resourceUrl})`)
          }

          // 2단계: GCS 업로드 성공 시 SQLite에 즉시 url 업데이트
          // 이를 통해 이후 재시도에서 중복 업로드 방지
          await individualSetLocalModified(data.localStringId, [
            { key: 'id', value: data.id },
            { key: 'url', value: resourceUrl },
            { key: 'modifiedFromLocal', value: false },
          ])

          // 촬영 사진을 앱 디렉토리로 복사
          await handleCapturedPhotoClone({ ...data, resourcePath: localFilePath })

          // 서버 파일명으로 캐시에 복사 (오프라인)
          await syncCacheWithServerUrl(localFilePath || '', resourceUrl)

          // 파일 업로드 성공 후, URL 업데이트 API 호출
          await APIServer.updateFacilityBranchPhoto([
            {
              id: parseInt(data.id.toString()),
              url: resourceUrl,
              memo: data.memo,
              sort_key: data.sort_key,
              drawing: data.drawing ?? null,
              fields_updated_at: data.fields_updated_at,
            },
          ])

          return { ...data, url: resourceUrl }
        }
      } else {
        // --- 단순 메타데이터 업데이트 (예: memo, sort_key 변경) ---
        executeCallback = async (data: FacilityPhoto): Promise<void> => {
          await APIServer.updateFacilityBranchPhoto([
            {
              id: parseInt(data.id.toString()),
              memo: data.memo,
              sort_key: data.sort_key,
              drawing: data.drawing ?? null,
              // 필드별 스탬프 → 서버가 더 최신 스탬프의 필드는 덮지 않는다 (_client_ 가드)
              fields_updated_at: data.fields_updated_at,
            },
          ])
        }
      }
    }

    syncQueue.addJob({
      localStringId: item.localStringId,
      data: item,
      priority: jobPriority,
      maxRetries: 5,
      tag: 'facilityPhoto',
      execute: executeCallback,

      onSuccess: async (result, data) => {
        if (!isNew) {
          const updateValues = [{ key: 'modifiedFromLocal', value: false }]
          if (needsFileUpload && result?.url) {
            updateValues.push({ key: 'url', value: result.url })
          }
          await individualSetLocalModified(data.localStringId, updateValues)
        }
      },

      onError: (error, data) => {
        // 파일 부재(FileMissingError)는 가드가 이미 로깅하므로 그 외 실패만 기록
        if (!(error instanceof FileMissingError)) {
          console.error(
            `[PhotoGuard] facilityPhoto job failed id=${data.localStringId}: ${error instanceof Error ? error.message : String(error)}`,
          )
        }
        console.error(`[SyncQueue] Facility image job for ${data.localStringId} FAILED.`, error)
      },

      onIdMap: isNew
        ? async (localStringId: string, serverResult: FacilityPhoto): Promise<void> => {
            // 중요: 데이터베이스에서 중복된 항목이 있는지 확인하고 제거
            const db = await SQL.getDatabase()
            const existingItems = await db.query(
              `SELECT * FROM ${storeKey} WHERE localStringId = ?`,
              [localStringId],
            )

            if (existingItems.values && existingItems.values.length > 1) {
              console.warn(
                `[SyncQueue] Found ${existingItems.values.length} duplicate items for ${localStringId}. Cleaning up...`,
              )
              // 가장 최신 항목을 제외한 나머지는 삭제
              await db.execute(
                `DELETE FROM ${storeKey} WHERE localStringId = ? AND id LIKE '%_need_to_upload'`,
                [localStringId],
              )
            }

            // 메모리 상의 데이터 업데이트
            const imageInStore = facilityPhotos.value.find(p => p.localStringId === localStringId)
            if (imageInStore) {
              imageInStore.id = serverResult.id
              imageInStore.url = serverResult.url
              imageInStore.modifiedFromLocal = false
            }

            // 데이터베이스 업데이트
            // (업로드 도중 로컬 삭제가 끼어든 경우 mfl을 유지해 삭제 job이 이어서 전파)
            const idMapKeys: { key: string; value: unknown }[] = [
              { key: 'id', value: serverResult.id },
              { key: 'url', value: serverResult.url },
            ]
            if (!(await isRowDeletedLocally(storeKey, localStringId))) {
              idMapKeys.push({ key: 'modifiedFromLocal', value: false })
            }
            await individualSetLocalModified(localStringId, idMapKeys)
          }
        : undefined,
    })
  }
  /**
   * 동일 localStringId의 유령 row(_need_to_upload 잔재)를 SQLite와 메모리에서 정리한다.
   * 병합 영속화 race가 만든 중복 row는 재업로드로 인한 서버 중복 레코드와
   * id 교체 시 PRIMARY KEY 충돌(UPDATE 전체 실패 → 재시도 루프)을 유발한다.
   * @param rows 검사할 row 목록
   * @returns 유령 row가 제거된 목록
   */
  async function removeGhostRows(rows: FacilityPhoto[]): Promise<FacilityPhoto[]> {
    const byLocalStringId = new Map<string, FacilityPhoto[]>()
    for (const row of rows) {
      const key = String(row.localStringId)
      const list = byLocalStringId.get(key)
      if (list) list.push(row)
      else byLocalStringId.set(key, [row])
    }

    const ghostIds = new Set<string>()
    const db = await SQL.getDatabase()
    for (const [key, list] of byLocalStringId) {
      if (list.length < 2) continue
      const ghosts = list.filter(row => String(row.id).includes('_need_to_upload'))
      const realRows = list.filter(row => !String(row.id).includes('_need_to_upload'))
      if (ghosts.length === 0 || realRows.length === 0) continue

      console.warn(
        `[PhotoGuard] ghost row 정리(facility): localStringId=${key} 유령 ${ghosts.length}개 삭제 (유지 id=${realRows.map(r => r.id).join(',')})`,
      )
      for (const ghost of ghosts) {
        await db.run(`DELETE FROM ${storeKey} WHERE id = ?`, [String(ghost.id)])
        ghostIds.add(String(ghost.id))
      }
      // 유지된 row가 모두 동기화 완료 상태면 유령 데이터로 대기 중인 job도 함께 제거
      if (realRows.every(row => !row.modifiedFromLocal)) {
        syncQueue.removeJobByLocalStringId(key)
      }
    }

    if (ghostIds.size === 0) return rows
    facilityPhotos.value = facilityPhotos.value.filter(photo => !ghostIds.has(String(photo.id)))
    return rows.filter(row => !ghostIds.has(String(row.id)))
  }

  async function initData(taskId: string, taskFacilityPhotos?: FacilityPhoto[]): Promise<void> {
    currentTaskDatabaseId.value = taskId
    let localData: FacilityPhoto[] = []
    let mergedData: FacilityPhoto[] = []
    const networkStatus = await Network.getStatus()
    if (isApp()) {
      localData = (await SQLite.getLocalData(storeKey, 'task_id', taskId, true)) as FacilityPhoto[]
      // 동일 localStringId의 유령 row가 남아 있으면 병합 전에 정리 (재업로드 차단)
      localData = await removeGhostRows(localData)
      // 초기값은 로컬 데이터
      mergedData = localData
      setupSQLiteListener(taskId)
    }
    if (networkStatus.connected) {
      return await getServerData(taskId, taskFacilityPhotos)
        .then(async serverData => {
          if (!isApp()) {
            return Merge.sortupdatedAt(serverData)
          }
          // 3. 테이블 생성 처리
          if (!(await SQLite.checkTableExists(storeKey, serverData))) {
            return serverData
          }

          return serverData
        })
        .then(async serverData => {
          if (!isApp()) {
            return serverData
          }
          // 서버가 빈 배열인데 로컬에 비삭제 데이터가 있으면 merge 스킵 (데이터 소실 방지)
          const activeLocalData = localData.filter(item => !item.deleted_at)
          if (serverData.length > 0 || activeLocalData.length === 0) {
            mergedData = Merge.mergeBothData(localData, serverData, storeKey)
          } else {
            mergedData = localData
          }
          return mergedData
        })
        .then(async mergedData => {
          // 5. 스토어 업데이트
          if (mergedData.length > 0) {
            facilityPhotos.value = mergedData
            if (!isApp()) return
            const convertedMergedData = mergedData.map(item => {
              const itemId = String(item.id)
              const itemLocalStringId = String(item.localStringId)

              const needsFileUpload =
                (item.url.startsWith('android') ||
                  item.url.startsWith('local-file://') ||
                  item.url.startsWith('data:image')) &&
                itemId != itemLocalStringId &&
                `${itemId}` == `${itemLocalStringId}_need_to_upload` &&
                !item.deleted_at &&
                // 부재 확정 사진은 업로드 대상으로 되살리지 않음
                !isFileMissingConfirmed(String(item.localStringId))
              return {
                ...item,
                modifiedFromLocal: item.modifiedFromLocal || needsFileUpload,
              }
            })
            // initData에서는 store가 이미 세팅되었으므로 emitUpdate와 addApiQueue를 생략한다
            const isAlreadySyncing = SyncManager.isTaskSyncing(taskId)
            if (!isAlreadySyncing) {
              const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(
                convertedMergedData as unknown as FacilityPhoto[],
                storeKey,
              )
              // 병합 중 업로드 job이 교체한 row의 옛 스냅샷은 저장 제외 (유령 row 부활 방지)
              const safeItems = await filterStaleMergedItems(
                itemsToProcess as { item: FacilityPhoto; itemId: string }[],
                storeKey,
                taskId,
              )
              await SQLite.updateSqliteOnly(
                safeItems,
                columns,
                currentTaskDatabaseId.value,
                storeKey,
                true,
              )
            } else {
              console.info(
                `[FacilityPhotoStore] taskId ${taskId}: backgroundSync 진행 중 - requestUpdate 스킵 (중복 방지)`,
              )
            }
          }
        })
        .catch(error => {
          throw error
        })
    } else {
      const sortData = Merge.sortupdatedAt(mergedData)
      facilityPhotos.value = sortData
    }
  }

  const getFacilityPhotoList = computed(() => {
    return (facilityBranchDatabaseId: string): FacilityPhoto[] => {
      const facilityPhotoList = facilityPhotos.value.filter(
        photo => !photo.deleted_at && photo.facility_id.toString() == facilityBranchDatabaseId,
      )
      if (facilityPhotoList.length > 0) {
        return sortByKeys(facilityPhotoList)
      }
      return []
    }
  })

  async function addFacilityPhoto(
    facilityId: string,
    file?: File | null,
    localFilePath?: LocalFileResourcePath,
  ): Promise<Partial<FacilityPhoto>> {
    if (!file && !localFilePath) throw new Error('이미지 경로 없음. 저장 실패')
    const dataTemp: Partial<FacilityPhoto> = {
      facility_id: parseInt(facilityId),
      memo: '',
      url: '',
      // 해당 시설물 사진 목록의 맨 뒤에 위치하도록 sort_key 부여 (오프라인/재정렬 안정성)
      sort_key: getLastSortKey(getFacilityPhotoList.value(facilityId)),
    }
    if (!isApp() && file) {
      try {
        const taskId = useAllTaskStore().currentTaskData?.id
        const fileName = `${getUnixtime()}_${file.name}`
        const uploadPath = uploadPathGenerator(taskId ? taskId.toString() : '', facilityId)
        const resourceUrl = await CloudFile.uploadFile(
          'homecheck-service',
          `${GCS_ROOT_PATH}/${uploadPath}/${fileName}`,
          file,
        )
        if (!resourceUrl) throw new Error('웹환경: 클라우드 업로드 실패')
        dataTemp.url = resourceUrl

        const [serverData] = await APIServer.addFacilityBranchPhoto([dataTemp])
        facilityPhotos.value.push({
          ...serverData,
          localStringId: serverData.id.toString(),
        } as FacilityPhoto)
      } catch (error) {
        console.error('웹환경: 사진 추가 실패:', error)
        throw error
      }
    } else if (localFilePath && isApp()) {
      try {
        const localFacilityBranchDatabaseId = getLocalDatabaseRandomKey()
        const currentTime = Merge.getCurrentTime()
        const initialFieldsUpdatedAt: { [key: string]: string } = {
          url: currentTime,
          memo: currentTime,
          sort_key: currentTime,
          facility_id: currentTime,
        }

        dataTemp.localStringId = localFacilityBranchDatabaseId
        dataTemp.id = `${localFacilityBranchDatabaseId}_need_to_upload`
        dataTemp.url = localFilePath
        dataTemp.task_id = parseInt(currentTaskDatabaseId.value)
        // 로컬 생성 행도 전역 동기화의 계정 필터에 매칭되도록 실제 company_id 부여
        dataTemp.company_id = useAuthStore().companyId ?? 0
        // dataTemp 초기화 시 시설물 단위로 부여한 sort_key 유지 (해당 목록 맨 뒤)
        dataTemp.fields_updated_at = initialFieldsUpdatedAt
        dataTemp.created_at = currentTime
        dataTemp.updated_at = currentTime
        dataTemp.modifiedFromLocal = true

        await requestUpdate([dataTemp as FacilityPhoto])
      } catch (error) {
        console.error('앱환경: 사진 추가 실패:', error)
        throw error
      }
    }

    return dataTemp as Partial<FacilityPhoto>
  }

  const getViewItemsForExport = computed(() => {
    return (
      currentFacilityBranchDatabaseId: string,
    ): { image: string; gcsUrl: string; content: string }[] => {
      const filtered = facilityPhotos.value
        .filter(photo => !photo.deleted_at)
        .filter(photo => photo.facility_id.toString() == currentFacilityBranchDatabaseId)
      // 화면 표시 순서와 동일하게 sort_key 기준 정렬 후 출력
      return sortByKeys(filtered).map(photo => {
        return {
          image: CloudFile.ResourceUrlToSignedUrl(photo.url),
          gcsUrl: photo.url,
          content: photo.memo,
        }
      })
    }
  })

  async function updateFacilityPhoto(id: string, memo: string): Promise<void> {
    let updatedPhotoRef: FacilityPhoto | undefined
    const findPhoto = facilityPhotos.value.find(
      photo => photo.id.toString() == id || photo.localStringId == id,
    )
    if (!findPhoto) return
    // 변경 없으면 불필요한 SQLite 쓰기/이벤트 방지
    if (findPhoto.memo === memo) return

    const changedPhoto = { ...findPhoto, memo }
    const changedFields = Merge.findChangedField(findPhoto, changedPhoto)

    if (isApp()) {
      updatedPhotoRef = {
        ...findPhoto,
        memo: memo,
        fields_updated_at: changedFields,
        modifiedFromLocal: true,
      } as FacilityPhoto
      await requestUpdate([updatedPhotoRef])
    } else {
      const [updatedPhoto] = await APIServer.updateFacilityBranchPhoto([
        { id: parseInt(id), memo: memo },
      ])
      updatedPhotoRef = updatedPhoto as FacilityPhoto
    }
    if (!updatedPhotoRef) return
    if (!isApp()) {
      facilityPhotos.value = facilityPhotos.value.map(photo => {
        if (photo.id.toString() == id || photo.localStringId == id) {
          return updatedPhotoRef as FacilityPhoto
        }
        return photo
      })
    }
  }

  /**
   * 시설물 사진 그리기 저장 (그림을 모두 지우면 null)
   * 앱: 스탬프 → SQLite → 동기화 큐, 웹: API 직접 호출.
   * deferSync면 로컬(앱 SQLite·웹 메모리)까지만 반영하고 서버 전송은 syncFacilityPhotoDrawing이 한다
   * — 서버 전송 디바운스는 저장 API(photoDrawing/store.ts)가 한다.
   * @param id 대상 사진의 id 또는 localStringId
   * @param drawing 그림 데이터, 그림 없음은 null
   * @param options.deferSync 서버 전송을 미룸
   */
  async function setFacilityPhotoDrawing(
    id: string,
    drawing: PhotoDrawing | null,
    options: { deferSync?: boolean } = {},
  ): Promise<void> {
    const findPhoto = facilityPhotos.value.find(
      photo => photo.id.toString() == id || photo.localStringId == id,
    )
    if (!findPhoto) return
    const nextDrawing = drawing ?? null
    // 기존 row에는 drawing 키가 없을 수 있다 — 키가 없으면 findChangedField가 스탬프를 찍지 않으므로 null로 맞춘다
    const before = { ...findPhoto, drawing: findPhoto.drawing ?? null }
    // 서버 jsonb는 키 순서를 바꿔 돌려주므로 순서 무관 비교
    if (isSamePhotoDrawing(before.drawing, nextDrawing)) return

    const changedPhoto = { ...before, drawing: nextDrawing }
    const changedFields = Merge.findChangedField(before, changedPhoto)

    if (isApp()) {
      const localPhoto = {
        ...changedPhoto,
        fields_updated_at: changedFields,
        modifiedFromLocal: true,
      }
      if (options.deferSync) {
        const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(
          [localPhoto] as unknown as FacilityPhoto[],
          storeKey,
        )
        await SQLite.updateSqliteOnly(
          itemsToProcess,
          columns,
          currentTaskDatabaseId.value,
          storeKey,
        )
      } else {
        await requestUpdate([localPhoto])
      }
      return
    }

    // 웹: 메모리 먼저 반영 (화면 즉시 갱신), 서버는 바로 또는 나중에
    facilityPhotos.value = facilityPhotos.value.map(photo =>
      photo.id.toString() == id || photo.localStringId == id
        ? ({ ...changedPhoto, fields_updated_at: changedFields } as FacilityPhoto)
        : photo,
    )
    if (!options.deferSync) await syncFacilityPhotoDrawing(id)
  }

  /**
   * setFacilityPhotoDrawing(deferSync)로 미뤄 둔 그림 변경을 서버로 보낸다
   * 앱: 동기화 큐 등록(대기 job은 실행 시 최신 row를 읽음), 웹: 현재 메모리 값으로 PUT
   * @param id 대상 사진의 id 또는 localStringId
   */
  async function syncFacilityPhotoDrawing(id: string): Promise<void> {
    const findPhoto = facilityPhotos.value.find(
      photo => photo.id.toString() == id || photo.localStringId == id,
    )
    if (!findPhoto || findPhoto.deleted_at) return

    if (isApp()) {
      // 스토어는 SQLite 이벤트로 비동기 갱신되므로 저장 직후 flush에서도 맞도록 SQLite row를 읽는다
      const db = await SQL.getDatabase()
      const result = await db.query(`SELECT * FROM ${storeKey} WHERE localStringId = ? LIMIT 1`, [
        findPhoto.localStringId,
      ])
      if (!result.values?.length) return
      const row = parseRow(result.values[0]) as FacilityPhoto
      if (row.deleted_at || !row.modifiedFromLocal) return
      await addApiQueue([{ item: row, itemId: row.localStringId }])
      return
    }

    const stamp = findPhoto.fields_updated_at?.drawing
    const [updatedPhoto] = await APIServer.updateFacilityBranchPhoto([
      {
        id: parseInt(findPhoto.id.toString()),
        drawing: findPhoto.drawing ?? null,
        ...(stamp ? { fields_updated_at: { drawing: stamp } } : {}),
      },
    ])
    if (!updatedPhoto) return
    facilityPhotos.value = facilityPhotos.value.map(photo => {
      if (photo.id.toString() != id && photo.localStringId != id) return photo
      // 서버 응답에는 localStringId 등 클라이언트 필드가 없으므로 기존 객체 위에 덮는다.
      // 전송 뒤 그림이 또 바뀌었으면(다음 디바운스 대기) 메모리의 새 그림을 지킨다
      if (!isSamePhotoDrawing(photo.drawing, findPhoto.drawing)) return photo
      return { ...photo, ...updatedPhoto } as FacilityPhoto
    })
  }

  /**
   * 시설물 사진의 sort_key를 갱신 (fractional indexing 기반 순서 변경)
   * @param id - 대상 사진의 id 또는 localStringId
   * @param sortKey - 새로 계산된 sort_key
   */
  async function updateFacilityPhotoSortKey(id: string, sortKey: string): Promise<void> {
    const findPhoto = facilityPhotos.value.find(
      photo => photo.id.toString() == id || photo.localStringId == id,
    )
    if (!findPhoto) return
    // 변경 없으면 불필요한 SQLite 쓰기/이벤트 방지
    if (findPhoto.sort_key === sortKey) return

    const changedPhoto = { ...findPhoto, sort_key: sortKey }
    const changedFields = Merge.findChangedField(findPhoto, changedPhoto)

    if (isApp()) {
      const updatedPhotoRef = {
        ...findPhoto,
        sort_key: sortKey,
        fields_updated_at: changedFields,
        modifiedFromLocal: true,
      } as FacilityPhoto
      await requestUpdate([updatedPhotoRef])
    } else {
      await APIServer.updateFacilityBranchPhoto([{ id: parseInt(id), sort_key: sortKey }])
      facilityPhotos.value = facilityPhotos.value.map(photo => {
        if (photo.id.toString() == id || photo.localStringId == id) {
          return changedPhoto
        }
        return photo
      })
    }
  }

  async function deleteFacilityPhoto(id: string): Promise<void> {
    const findPhoto = facilityPhotos.value.find(
      photo => photo.id.toString() == id || photo.localStringId == id,
    )
    if (!findPhoto) return

    const changedPhoto = { ...findPhoto }
    changedPhoto.deleted_at = Merge.getCurrentTime()
    if (isApp()) {
      changedPhoto.modifiedFromLocal = true
      await requestUpdate([changedPhoto])
    } else {
      await APIServer.deleteFacilityBranchPhoto([{ id: parseInt(id) }])
    }
    if (!isApp()) {
      facilityPhotos.value = facilityPhotos.value.map(photo => {
        if (photo.id.toString() == id || photo.localStringId == id) {
          return { ...changedPhoto }
        }
        return photo
      })
    }
  }

  function allClear(): void {
    // 리스너 해제
    if (currentListener) {
      SQLiteEventBus.getInstance().offTableUpdated('facility_photos', currentListener)
      currentListener = null
    }
    facilityPhotos.value = []
  }

  async function getAllModifiedData(): Promise<FacilityPhoto[]> {
    const db = await SQL.getDatabase()
    const localData = await db.query(`SELECT * FROM ${storeKey} WHERE modifiedFromLocal = 1`)
    return localData.values?.map((row: FacilityPhoto) => parseRow(row)) as FacilityPhoto[]
  }
  return {
    facilityPhotos,
    getFacilityPhotoList,
    notUploadedFacilityPhotos,
    initData,
    uploadNotUploaded,
    requestUpdate,
    allClear,
    getAllModifiedData,
    addApiQueue,
    getFacilityPhotoListOnTask,
    addFacilityPhoto,
    updateFacilityPhoto,
    updateFacilityPhotoSortKey,
    setFacilityPhotoDrawing,
    syncFacilityPhotoDrawing,
    deleteFacilityPhoto,
    getAllFacilityBranchImages,
    getViewItemsForExport,
  }
})
